import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Bell,
  Mail,
  MessageSquare,
  Send,
  Loader2,
  Clock,
  CheckCircle,
  AlertCircle,
  Plus
} from "lucide-react";

const NotificationsPage = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [newNotification, setNewNotification] = useState({
    user_id: "",
    type: "email",
    title: "",
    message: "",
    priority: "normal"
  });

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des notifications");
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotification = async () => {
    if (!newNotification.user_id || !newNotification.title || !newNotification.message) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/notifications/send`, newNotification, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Notification envoyée avec succès!");
      setShowSendModal(false);
      setNewNotification({
        user_id: "",
        type: "email",
        title: "",
        message: "",
        priority: "normal"
      });
      fetchNotifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      email: Mail,
      sms: MessageSquare,
      whatsapp: MessageSquare
    };
    return icons[type] || Bell;
  };

  const getTypeColor = (type) => {
    const colors = {
      email: "bg-blue-500/100/10 text-blue-400",
      sms: "bg-emerald-500/10 text-emerald-400",
      whatsapp: "bg-green-500/100/10 text-green-400"
    };
    return colors[type] || "bg-white/50/10 text-slate-400";
  };

  const getStatusBadge = (status) => {
    const styles = {
      sent: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
      failed: "bg-red-500/100/20 text-red-400 border-red-500/30",
      pending: "bg-amber-500/100/20 text-amber-400 border-amber-500/30"
    };
    const labels = {
      sent: "Envoyé",
      failed: "Échec",
      pending: "En attente"
    };
    return (
      <Badge variant="outline" className={styles[status] || styles.pending}>
        {labels[status] || status}
      </Badge>
    );
  };

  const isAdmin = user?.role === "admin";

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-6" data-testid="notifications-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-outfit font-bold text-white">
              Centre de Notifications
            </h1>
            <p className="text-slate-400 mt-1">
              Historique des notifications (Email, SMS, WhatsApp)
            </p>
          </div>
          
          {isAdmin && (
            <Dialog open={showSendModal} onOpenChange={setShowSendModal}>
              <DialogTrigger asChild>
                <Button 
                  data-testid="send-notification-btn"
                  className="btn-primary gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Envoyer une notification
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-white/10 max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-white font-outfit">Nouvelle Notification</DialogTitle>
                  <DialogDescription className="text-slate-400">
                    Envoyez une notification via Email, SMS ou WhatsApp (Mode Mock)
                  </DialogDescription>
                </DialogHeader>
                
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Type</Label>
                      <Select
                        value={newNotification.type}
                        onValueChange={(v) => setNewNotification({...newNotification, type: v})}
                      >
                        <SelectTrigger className="bg-slate-950/50 border-white/10 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-white/10">
                          <SelectItem value="email" className="text-slate-300">
                            <span className="flex items-center gap-2">
                              <Mail className="w-4 h-4" /> Email
                            </span>
                          </SelectItem>
                          <SelectItem value="sms" className="text-slate-300">
                            <span className="flex items-center gap-2">
                              <MessageSquare className="w-4 h-4" /> SMS
                            </span>
                          </SelectItem>
                          <SelectItem value="whatsapp" className="text-slate-300">
                            <span className="flex items-center gap-2">
                              <MessageSquare className="w-4 h-4" /> WhatsApp
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-slate-300">Priorité</Label>
                      <Select
                        value={newNotification.priority}
                        onValueChange={(v) => setNewNotification({...newNotification, priority: v})}
                      >
                        <SelectTrigger className="bg-slate-950/50 border-white/10 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-white/10">
                          <SelectItem value="normal" className="text-slate-300">Normal</SelectItem>
                          <SelectItem value="urgent" className="text-slate-300">Urgent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-slate-300">
                      {newNotification.type === 'email' ? 'Adresse email' : 'Numéro de téléphone'}
                    </Label>
                    <Input
                      value={newNotification.user_id}
                      onChange={(e) => setNewNotification({...newNotification, user_id: e.target.value})}
                      placeholder={newNotification.type === 'email' ? 'destinataire@email.com' : '+225 07 XX XX XX XX'}
                      data-testid="notification-recipient-input"
                      className="bg-slate-950/50 border-white/10 text-white"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-slate-300">Titre</Label>
                    <Input
                      value={newNotification.title}
                      onChange={(e) => setNewNotification({...newNotification, title: e.target.value})}
                      placeholder="Objet de la notification"
                      data-testid="notification-title-input"
                      className="bg-slate-950/50 border-white/10 text-white"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-slate-300">Message</Label>
                    <Textarea
                      value={newNotification.message}
                      onChange={(e) => setNewNotification({...newNotification, message: e.target.value})}
                      placeholder="Contenu du message..."
                      data-testid="notification-message-input"
                      className="bg-slate-950/50 border-white/10 text-white h-24 resize-none"
                    />
                  </div>

                  <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                    <p className="text-emerald-400 text-sm flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Infobip connecté: SMS et WhatsApp réels activés
                    </p>
                  </div>
                </div>
                
                <DialogFooter>
                  <Button 
                    variant="ghost" 
                    onClick={() => setShowSendModal(false)}
                    className="text-slate-400"
                  >
                    Annuler
                  </Button>
                  <Button 
                    onClick={handleSendNotification}
                    disabled={sending}
                    data-testid="confirm-send-notification-btn"
                    className="btn-primary gap-2"
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Envoyer
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Notification Channels Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="glass-card border-sky-500/20">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-sky-500/10 rounded-xl">
                <Mail className="w-6 h-6 text-sky-400" />
              </div>
              <div>
                <p className="text-white font-medium">Email (Infobip)</p>
                <p className="text-emerald-400 text-sm">✓ Connecté</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-card border-emerald-500/20">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-emerald-500/10 rounded-xl">
                <MessageSquare className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-white font-medium">SMS (Infobip)</p>
                <p className="text-emerald-400 text-sm">✓ Connecté</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-card border-green-500/20">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-green-500/100/10 rounded-xl">
                <MessageSquare className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-white font-medium">WhatsApp (Infobip)</p>
                <p className="text-emerald-400 text-sm">✓ Connecté</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Notifications List */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-white font-outfit flex items-center gap-2">
              <Bell className="w-5 h-5 text-sky-400" />
              Historique des Notifications
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
              </div>
            ) : notifications.length > 0 ? (
              <div className="space-y-3">
                {notifications.map((notif) => {
                  const TypeIcon = getTypeIcon(notif.type);
                  return (
                    <div 
                      key={notif.id}
                      className="flex items-start gap-4 p-4 rounded-lg bg-slate-950/50 border border-white/5"
                    >
                      <div className={`p-2 rounded-lg ${getTypeColor(notif.type)}`}>
                        <TypeIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-white font-medium">{notif.title}</p>
                          {notif.priority === 'urgent' && (
                            <Badge className="bg-red-500/100/20 text-red-400 border-red-500/30">
                              Urgent
                            </Badge>
                          )}
                        </div>
                        <p className="text-slate-400 text-sm line-clamp-2">{notif.message}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(notif.created_at).toLocaleString('fr-FR')}
                          </span>
                          <span className="capitalize">{notif.type}</span>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        {getStatusBadge(notif.status)}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Bell className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">Aucune notification</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default NotificationsPage;
